// src/pages/marketplace/developer/DeveloperMarketplacePage.tsx
// Edit marketplace listing metadata for a developer product.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Textarea,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Badge,
  ErrorAlert,
} from '@react/components';

const TABS = [
  { key: 'edit', label: 'General', path: (id: string) => `/developer/products/${id}/edit` },
  { key: 'marketplace', label: 'Marketplace', path: (id: string) => `/developer/products/${id}/marketplace` },
  { key: 'dependencies', label: 'Dependencies', path: (id: string) => `/developer/products/${id}/dependencies` },
  { key: 'reviews', label: 'Reviews', path: (id: string) => `/developer/products/${id}/reviews` },
  { key: 'publish', label: 'Publish', path: (id: string) => `/developer/products/${id}/publish` },
] as const;

interface MetadataForm {
  short_description: string;
  long_description: string;
  changelog: string;
  screenshot_urls: string[];
  video_url: string;
  demo_url: string;
  tags: string;
  supported_locales: string;
  version: string;
}

export const DeveloperMarketplacePage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useProduct();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newScreenshotUrl, setNewScreenshotUrl] = useState('');
  const [formData, setFormData] = useState<MetadataForm>({
    short_description: '',
    long_description: '',
    changelog: '',
    screenshot_urls: [],
    video_url: '',
    demo_url: '',
    tags: '',
    supported_locales: '',
    version: '',
  });

  useEffect(() => {
    if (id) {
      store.GetProduct(id);
      store.GetMarketplaceMetadata(id);
    }
    return () => {
      store.clearCurrent();
    };
  }, [id]);

  useEffect(() => {
    const metadata = (store as any).marketplaceMetadata ?? (store as any).currentMetadata;
    if (metadata) {
      setFormData({
        short_description: metadata.short_description ?? '',
        long_description: metadata.long_description ?? '',
        changelog: metadata.changelog ?? '',
        screenshot_urls: Array.isArray(metadata.screenshot_urls) ? metadata.screenshot_urls : [],
        video_url: metadata.video_url ?? '',
        demo_url: metadata.demo_url ?? '',
        tags: Array.isArray(metadata.tags) ? metadata.tags.join(', ') : (metadata.tags ?? ''),
        supported_locales: Array.isArray(metadata.supported_locales)
          ? metadata.supported_locales.join(', ')
          : (metadata.supported_locales ?? ''),
        version: metadata.version ?? '',
      });
    }
  }, [(store as any).marketplaceMetadata, (store as any).currentMetadata]);

  const handleChange = (field: keyof MetadataForm, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddScreenshot = () => {
    const url = newScreenshotUrl.trim();
    if (!url) return;
    setFormData((prev) => ({
      ...prev,
      screenshot_urls: [...prev.screenshot_urls, url],
    }));
    setNewScreenshotUrl('');
  };

  const handleRemoveScreenshot = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      screenshot_urls: prev.screenshot_urls.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    if (!id) return;
    setIsSubmitting(true);

    try {
      await store.UpdateMarketplaceMetadata(id, {
        short_description: formData.short_description,
        long_description: formData.long_description,
        changelog: formData.changelog,
        screenshot_urls: formData.screenshot_urls,
        video_url: formData.video_url || undefined,
        demo_url: formData.demo_url || undefined,
        tags: formData.tags
          ? formData.tags.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        supported_locales: formData.supported_locales
          ? formData.supported_locales.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        version: formData.version,
      } as any);
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const productName = (store.currentItem as any)?.name || 'Product';

  if (store.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb. The tab already names this page; the heading stays for screen readers. */}
      <div>
        <nav className="text-sm">
          <Link to="/developer" className="text-muted-foreground hover:text-foreground">
            Developer
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/developer/products/${id}/edit`} className="text-muted-foreground hover:text-foreground">
            {productName}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">Marketplace Listing</span>
        </nav>
        <h1 className="sr-only">Marketplace Listing</h1>
      </div>

      {/* Tab navigation */}
      <nav className="flex border-b border-border">
        {TABS.map((tab) => (
          <Link
            key={tab.key}
            to={tab.path(id || '')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab.key === 'marketplace'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            {tab.label}
          </Link>
        ))}
      </nav>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Descriptions */}
      <Card>
        <CardHeader>
          <CardTitle>Descriptions</CardTitle>
          <CardDescription>Content displayed on the marketplace listing page.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label="Short Description"
            type="text"
            value={formData.short_description}
            onChange={(e) => handleChange('short_description', e.target.value)}
            placeholder="Brief one-line description for search results"
          />
          <Textarea
            label="Long Description"
            value={formData.long_description}
            onChange={(e) => handleChange('long_description', e.target.value)}
            placeholder="Detailed description of your product. Supports markdown."
            rows={10}
          />
          <Textarea
            label="Changelog"
            value={formData.changelog}
            onChange={(e) => handleChange('changelog', e.target.value)}
            placeholder="## v1.0.0&#10;- Initial release"
            rows={6}
          />
        </CardContent>
      </Card>

      {/* Media */}
      <Card>
        <CardHeader>
          <CardTitle>Media</CardTitle>
          <CardDescription>Screenshots, video, and demo links for the listing.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Screenshots */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-2">Screenshots</label>
            {formData.screenshot_urls.length > 0 ? (
              <div className="space-y-2 mb-3">
                {formData.screenshot_urls.map((url, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-2 rounded-lg border border-border bg-muted/30"
                  >
                    <img
                      src={url}
                      alt={`Screenshot ${index + 1}`}
                      className="w-16 h-10 object-cover rounded border border-border"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <span className="flex-1 text-sm text-foreground truncate font-mono">{url}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive flex-shrink-0"
                      onClick={() => handleRemoveScreenshot(index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mb-3">No screenshots added yet.</p>
            )}
            <div className="flex gap-2">
              <Input
                type="url"
                value={newScreenshotUrl}
                onChange={(e) => setNewScreenshotUrl(e.target.value)}
                placeholder="https://example.com/screenshot.png"
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddScreenshot();
                  }
                }}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={handleAddScreenshot}
                disabled={!newScreenshotUrl.trim()}
              >
                Add
              </Button>
            </div>
          </div>

          <Input
            label="Video URL"
            type="url"
            value={formData.video_url}
            onChange={(e) => handleChange('video_url', e.target.value)}
            placeholder="https://youtube.com/watch?v=..."
          />
          <Input
            label="Demo URL"
            type="url"
            value={formData.demo_url}
            onChange={(e) => handleChange('demo_url', e.target.value)}
            placeholder="https://demo.myproduct.com"
          />
        </CardContent>
      </Card>

      {/* Tags & Locales */}
      <Card>
        <CardHeader>
          <CardTitle>Tags & Locales</CardTitle>
          <CardDescription>Categorization and language support information.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Input
              label="Tags"
              type="text"
              value={formData.tags}
              onChange={(e) => handleChange('tags', e.target.value)}
              placeholder="productivity, automation, integration"
            />
            <p className="text-xs text-muted-foreground mt-1">Comma-separated tags for discoverability.</p>
            {formData.tags && (
              <div className="flex flex-wrap gap-1 mt-2">
                {formData.tags.split(',').map((tag) => tag.trim()).filter(Boolean).map((tag, i) => (
                  <Badge key={i} variant="secondary">{tag}</Badge>
                ))}
              </div>
            )}
          </div>
          <div>
            <Input
              label="Supported Locales"
              type="text"
              value={formData.supported_locales}
              onChange={(e) => handleChange('supported_locales', e.target.value)}
              placeholder="en, ru, de, fr"
            />
            <p className="text-xs text-muted-foreground mt-1">Comma-separated locale codes.</p>
            {formData.supported_locales && (
              <div className="flex flex-wrap gap-1 mt-2">
                {formData.supported_locales.split(',').map((l) => l.trim()).filter(Boolean).map((locale, i) => (
                  <Badge key={i} variant="default">{locale}</Badge>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Version */}
      <Card>
        <CardHeader>
          <CardTitle>Version</CardTitle>
          <CardDescription>Current published version of the marketplace listing.</CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            label="Version"
            type="text"
            value={formData.version}
            onChange={(e) => handleChange('version', e.target.value)}
            placeholder="1.0.0"
          />
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? t('common.saving') : 'Save Marketplace Listing'}
        </Button>
      </div>
    </div>
  );
});

export default DeveloperMarketplacePage;
